import $ from 'jquery';
import React from 'react';
import { string } from 'prop-types';
import { createBrowserHistory } from 'history';
import { getQueryParams } from 'utils/query-param-helpers';
import { attachEnterAsClickDecorator } from 'utils/attachEnterAsClick';
import scrollTo from 'utils/scroll-to';
import Loader from '../loader';
import { initActiveFilters, manageActiveFilters, removeActiveFilterGroup, getActiveFiltersWithValues, removeActiveFilterExcludeGroup } from './utils/active-filters';
import { parseQueryString, buildQueryString } from './utils/query-string';
import FiltersSection from './filters/';
import ToolbarSection from './toolbar/';
import ListingItemsSection from './items/';
import { filterItems, paginateItems, sortItems } from './utils/item-filter';

export default class FilteredListing extends React.Component {
    constructor(props) {
        super(props);
        this.history = createBrowserHistory();
        this.state = {
            isLoading: true,
            data: {
                filterOptions: [],
                contentData: [],
                resources: [],
                settings: {}
            },
            APIStatus: '',
            APIMessage: '',
            activeFilters: [],
            currentSort: 'default',
            currentPage: 0, // page 1 is 0 (array index value)
            currentPageSize: 0,
            maxDate: new Date().getTime(),
            hasScrolled: false
        };

        this.toggleFilters = this.toggleFilters.bind(this);
        this.resetFilters = this.resetFilters.bind(this);
        this.resetFilterGroup = this.resetFilterGroup.bind(this);
        this.resetFilterExcludeGroup = this.resetFilterExcludeGroup.bind(this);
        this.changePage = this.changePage.bind(this);
        this.updateSort = this.updateSort.bind(this);
        this.toggleFocus = this.toggleFocus.bind(this);
        this.showMore = this.showMore.bind(this);
        this.scrollElRef = React.createRef();
    }

    initializeData() {
        const pageData = this.props.data.trim();

        // check for JSON data on the page,
        // If it exists then we're getting data from local mock json file
        if (pageData && pageData.length) {
            const newState = {
                ...this.state,
                data: { ...this.state.data }
            };
            // convert the page contents to JSON format & set to state
            try {
                const jsonPageData = JSON.parse(pageData);
                this.setData(jsonPageData, newState);

                // hide the loader if we have items from mock JSON page data
                if (jsonPageData.contentData && jsonPageData.contentData.length > 0) {
                    newState.isLoading = false;
                } else {
                    // Even if no contentData, we have valid JSON structure, so stop loading
                    newState.isLoading = false;
                }

                newState.activeFilters = initActiveFilters(jsonPageData.filterOptions);

                this.prePopulateFilters(newState);
                // update the component state with new preset filter values
                this.setState(prevState => ({ ...prevState, ...newState }));
            } catch (error) {
                console.error('Error parsing JSON data:', error);
                console.error('Invalid JSON data:', pageData.substring(0, 500) + '...');
                // Set error state instead of crashing
                this.setState({
                    isLoading: false,
                    APIStatus: 'error',
                    APIMessage: 'Invalid JSON data format'
                });
            }
        }
    }

    componentDidMount() {
        // Initialize data if available
        this.initializeData();
        
        const newState = {
            ...this.state,
            data: { ...this.state.data }
        };
        // If the app is still loading then get data from API
        if (this.state.isLoading && this.props.url && this.props.url !== '#') {
            // fetch data from API endpoint
            this.fetchItems(this.props.url, newState);
        } else {
            // for local that use JSON on structure data
            this.checkScrolling();
        }
    }

    componentDidUpdate(prevProps, prevState) {
        const queryParams = buildQueryString(this.state);

        this.history.replace({
            pathname: window.location.pathname,
            search: queryParams
        });

        if (prevState.currentPage !== this.state.currentPage) {
            scrollTo(document.body, 0, 200);
        }
        
        // Need to check after data fetched and dom available
        this.checkScrolling();
    }

    setData(data, newState) {
        // Create new data object to avoid mutation
        newState.data = {
            ...newState.data,
            contentData: data.contentData,
            settings: data.settings
        };

        // set filters if they exist
        if (data.filterOptions && data.filterOptions.length) {
            newState.data.filterOptions = data.filterOptions;
        }

        // set resources if they exist
        if (data.resources && data.resources.length) {
            newState.data.resources = data.resources;
        }
    }

    checkScrolling() {
        if (!this.state.hasScrolled) {
            // Need to be on componentDidUpdate for the ref to get acknowledged first
            const url = getQueryParams();
            if (url) {
                if (this.scrollElRef.current) {
                    $('html, body').animate({ scrollTop: ($(this.scrollElRef.current).offset().top - 20) }, 1000);
                    // eslint-disable-next-line react/no-did-update-set-state
                    this.setState({ hasScrolled: true });
                }
            } else {
                // eslint-disable-next-line react/no-did-update-set-state
                this.setState({ hasScrolled: true });
            }
        }
    }

    validateFilterValues(activeFilters, originalFilterOptions) {
        // Only validate categories filter, leave other filters unchanged
        return activeFilters.map(filter => {
            // Only validate the 'categories' filter
            if (filter.queryParam === 'categories') {
                // Find the original categories filter options
                const originalCategoriesFilter = originalFilterOptions.find(f => f.queryParam === 'categories');
                const validValues = originalCategoriesFilter && originalCategoriesFilter.values
                    ? originalCategoriesFilter.values
                    : [];

                // Filter out invalid category values
                const filteredValues = filter.values.filter(value => validValues.includes(value));

                return {
                    ...filter,
                    values: filteredValues
                };
            }

            // Return other filters unchanged
            return filter;
        });
    }

    prePopulateFilters(newState) {
        const url = getQueryParams();
        const activeFilters = newState.activeFilters.length > 0 ? newState.activeFilters : initActiveFilters(newState.data.filterOptions);
        const presetFilters = parseQueryString(url, activeFilters, this.state.currentPage);

        // Ensure settings object exists before accessing properties
        const settings = newState.data.settings || {};

        // If showAllResults is set to true, force all listing content to show on page load.
        if (settings.showAllResults) {
            newState.currentPageSize = 1000;
        } else {
            newState.currentPageSize = settings.itemsPerPage || 10; // Default fallback
        }

        if (presetFilters) {
            // Validate and filter out invalid category values
            if (presetFilters.activeFilters && presetFilters.activeFilters.length > 0) {
                const validatedFilters = this.validateFilterValues(presetFilters.activeFilters, newState.data.filterOptions);
                newState.activeFilters = validatedFilters;
            }

            if (presetFilters.currentSort && presetFilters.currentSort !== newState.currentSort) {
                newState.currentSort = presetFilters.currentSort;
            }

            if (presetFilters.currentPage && parseInt(presetFilters.currentPage, 10) !== 0) {
                newState.currentPage = parseInt(presetFilters.currentPage, 10);
            }
        }
    }

    fetchItems(url, newState) {
        $.getJSON(`/api/${url}`)
            .fail((jqxhr, textStatus, error) => {
                newState.APIStatus = 'error';
                newState.APIMessage = `${textStatus}, ${error}`;
                newState.isLoading = false; // Stop loading on error
                // update the app state with api error values
                this.setState(prevState => ({ ...prevState, ...newState }));
            })
            .done(response => {
                this.setData(response.data, newState);

                newState.isLoading = false;

                newState.activeFilters = initActiveFilters(response.data.filterOptions);

                this.prePopulateFilters(newState);

                // update the app state with api data & new preset filter values
                this.setState(prevState => ({ ...prevState, ...newState }));
            });
    }

    resetFilters(event) {
        event.preventDefault();
        this.setState({
            activeFilters: initActiveFilters(this.state.data.filterOptions),
            currentPage: 0
        });
    }

    resetFilterGroup(groupCode, returnData = false) {
        if (returnData) {
            return removeActiveFilterGroup(this.state.activeFilters, groupCode);
        }

        this.setState({
            activeFilters: removeActiveFilterGroup(this.state.activeFilters, groupCode),
            currentPage: 0
        });

        return true;
    }

    resetFilterExcludeGroup(groupCode, returnData = false) {
        if (returnData) {
            return removeActiveFilterExcludeGroup(this.state.activeFilters, groupCode);
        }

        this.setState({
            activeFilters: removeActiveFilterExcludeGroup(this.state.activeFilters, groupCode),
            currentPage: 0
        });

        return true;
    }

    toggleFilters(evt, filter, value, forceClear = false, modifiedActiveFilters = []) {
        if (typeof (evt.preventDefault) === 'function') {
            evt.preventDefault();
        }
        this.setState({
            activeFilters: manageActiveFilters(
                modifiedActiveFilters.length > 0 ? modifiedActiveFilters : this.state.activeFilters,
                filter.code,
                value,
                forceClear
            ),
            currentPage: 0
        });
    }

    changePage(pageNum) {
        this.setState({ currentPage: pageNum });
    }

    updateSort(sort) {
        this.setState({
            currentSort: sort,
            currentPage: 0
        });
    }

    showMore(event) {
        event.preventDefault();
        this.setState({ currentPageSize: this.state.currentPageSize + this.state.data.settings.itemsPerPage });
    }

    toggleFocus(filter) {
        this.setState({ focusedFilter: filter ? filter.code : null });
    }

    renderAPIErrorMessage() {
        if (this.state.APIStatus !== 'error') { return null; }

        return (
            <p className="validation-msg">Oops, something went wrong: {this.state.APIMessage}</p>
        );
    }

    render() {
        if (this.state.isLoading) {
            return (
                <div>
                    <Loader />
                    {this.renderAPIErrorMessage()}
                </div>
            );
        }

        const { contentData, filterOptions, resources, settings } = this.state.data;
        const { filtersLayout, tileContainerColumnClass, tileContainerTilesWrap, tileType } = this.props;
        
        // Provide default values for settings to prevent undefined prop errors
        const settingsWithDefaults = {
            resultTitle: '',
            showResultCount: false,
            showActiveFilters: true,
            showResetFilter: false,
            activeFiltersLabel: 'Current Filters:',
            filterLabel: '',
            keywordPlaceholderText: 'Search',
            keywordSearchableFields: ['title', 'subtext', 'paragraph'],
            showMoreButtonLabel: 'Load More',
            showMoreButtonType: false,
            hideInputFilter: false,
            limitedDate: false,
            itemsPerPage: 10,
            loadMoreOnScroll: false,
            sortBy: [],
            ...settings
        };
        
        const filterSettingsObj = {
            resultTitle: settingsWithDefaults.resultTitle,
            activeFiltersStyle: settingsWithDefaults.activeFiltersStyle ? settingsWithDefaults.activeFiltersStyle : 'btn-tags',
            showActiveFilters: settingsWithDefaults.showActiveFilters,
            showResetFilter: settingsWithDefaults.showResetFilter,
            resetFilterExcludeGroupCode: settingsWithDefaults.resetFilterExcludeGroupCode,
            activeFiltersLabel: settingsWithDefaults.activeFiltersLabel,
            filterLabel: settingsWithDefaults.filterLabel,
            keywordPlaceholderText: settingsWithDefaults.keywordPlaceholderText,
            keywordSearchableFields: settingsWithDefaults.keywordSearchableFields,
            showMoreButtonLabel: settingsWithDefaults.showMoreButtonLabel,
            showMoreButtonType: settingsWithDefaults.showMoreButtonType,
            hideInputFilter: settingsWithDefaults.hideInputFilter,
            limitedDate: settingsWithDefaults.limitedDate,
            itemsPerPage: settingsWithDefaults.itemsPerPage,
            loadMoreOnScroll: settingsWithDefaults.loadMoreOnScroll
        };

        const activeFilters = getActiveFiltersWithValues(this.state.activeFilters);
        const filteredItems = filterItems(activeFilters, contentData, filterSettingsObj.keywordSearchableFields);
        const sortedItems = sortItems(filteredItems, this.state.currentSort);
        let pagedItems = paginateItems(sortedItems, this.state.currentPage, this.state.currentPageSize);

        if (document.querySelector('.s-base-section.events')) {
            // if first load or the filter not contain past event filter, remove past event content from event listing.
            // only show past event content in past event category.
            if (!activeFilters.length ||
                !activeFilters[0].values.some(value => value.toUpperCase() === 'Past Events'.toUpperCase())) {
                const sortedEventItems = sortedItems.filter(item => item.pastEvent === false);
                pagedItems = paginateItems(sortedEventItems, this.state.currentPage, this.state.currentPageSize);
            }
        }

        return (
            <div className={`layout-${filtersLayout}`} ref={this.scrollElRef}>
                <div className="layout-filters-side-row">
                    {filterOptions.length > 0 &&
                        <div className="filters-wrapper">
                            <FiltersSection
                                filters={filterOptions}
                                filterSettings={filterSettingsObj}
                                activeFilters={activeFilters}
                                toggleFilters={attachEnterAsClickDecorator(this.toggleFilters)}
                                resetFilterGroup={this.resetFilterGroup}
                                focusedFilter={this.state.focusedFilter}
                                toggleFocus={this.toggleFocus}
                                maxDate={this.state.maxDate}
                                resetFilters={this.resetFilters}
                            />
                        </div>
                    }

                    <div className="listing-wrapper">
                        <ToolbarSection
                            showResultCount={settingsWithDefaults.showResultCount}
                            itemCount={filteredItems.length}
                            sortBy={settingsWithDefaults.sortBy}
                            currentSort={this.state.currentSort}
                            updateSort={this.updateSort}
                        />
                        <ListingItemsSection
                            resultTitle={filterSettingsObj.resultTitle}
                            listingItems={pagedItems}
                            resources={resources}
                            tileContainerColumnClass={tileContainerColumnClass}
                            tileContainerTilesWrap={tileContainerTilesWrap}
                            tileType={tileType}
                            changePage={this.changePage}
                            resetFilters={this.resetFilters}
                            showMoreItems={this.showMore}
                            showingItems={filteredItems.length > this.state.currentPageSize
                                ? filterSettingsObj.itemsPerPage
                                : filteredItems.length}
                            totalItems={filteredItems.length}
                            showMoreButtonLabel={filterSettingsObj.showMoreButtonLabel}
                            showMoreButtonType={filterSettingsObj.showMoreButtonType}
                            showMoreOnScroll={filterSettingsObj.loadMoreOnScroll}
                        />
                    </div>
                </div>
            </div>
        );
    }
}

FilteredListing.propTypes = {
    /* eslint-disable react/no-typos */
    // The disable is needed until https://github.com/yannickcr/eslint-plugin-react/issues/1389 is resolved
    url: string,
    data: string,
    tileType: string,
    tileContainerColumnClass: string,
    tileContainerTilesWrap: string,
    filtersLayout: string
    /* eslint-enable react/no-typos */
};

FilteredListing.defaultProps = {
    url: '/',
    data: '',
    tileType: 'base-tile',
    tileContainerColumnClass: 'col-three',
    tileContainerTilesWrap: 'tiles-wrap',
    filtersLayout: 'filters-top'
};
